import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { lazyWithPreload } from "utils/lazyWithPreload";

import { usePreloadComponents } from "hooks/usePreloadedComponents";
import { CreateEngineInput } from "services/engines/createWorkspaceEngine";
import {
  EditEngineInput,
  editWorkspaceEngine,
} from "services/engines/editWorkspaceEngine";
import { WorkspaceEngine } from "services/engines/engine.types";
import { ReactQueryKeysAccount } from "services/queryKeys";

import { StatusMessageType } from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";

const EngineModal = lazyWithPreload(
  () => import("../../EngineModal/EngineModal")
);

export const useEditEngine = () => {
  const [editEngine, setEditEngine] = useState<WorkspaceEngine | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const { putStatusMessage } = useStatusMessageQueue();

  usePreloadComponents([EngineModal]);

  const openEditEngine = (engine: WorkspaceEngine) => {
    setEditEngine(engine);
  };

  const closeEditEngine = () => {
    if (isLoading) {
      return;
    }
    setEditEngine(null);
  };

  const confirmEdit = async (engine: EditEngineInput) => {
    try {
      setIsLoading(true);
      await editWorkspaceEngine({
        engine,
        initialEngine: editEngine as WorkspaceEngine,
      });
      await queryClient.cancelQueries({
        queryKey: [ReactQueryKeysAccount.workspaceEngines],
      });

      queryClient.setQueriesData<WorkspaceEngine[] | undefined>(
        {
          queryKey: [ReactQueryKeysAccount.workspaceEngines],
        },
        workspaceEngines => {
          return (workspaceEngines || []).map(workspaceEngine => {
            if (
              workspaceEngine.engineName === engine.engineName ||
              workspaceEngine.engineName === editEngine?.engineName
            ) {
              return { ...workspaceEngine, ...engine };
            }
            return workspaceEngine;
          });
        }
      );

      putStatusMessage({
        message: t("workspace.edit_modal.success", {
          engineName: engine.engineName,
        }),
        type: StatusMessageType.Success,
      });
      setIsLoading(false);
      setEditEngine(null);
    } catch (error: any) {
      console.log(error);
      setIsLoading(false);
      putStatusMessage({
        message: error.message,
        type: StatusMessageType.Error,
      });
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysAccount.workspaceEngines],
      });
    }
  };

  const editEngineMarkup = editEngine ? (
    <EngineModal
      onClose={closeEditEngine}
      onSubmit={(engine: EditEngineInput | CreateEngineInput) => {
        confirmEdit(engine as EditEngineInput);
      }}
      isLoading={isLoading}
      engine={editEngine}
      title={t("workspace.edit_engine.title")}
      primaryButton="workspace.edit_engine.submit"
    />
  ) : null;

  return {
    editEngineMarkup,
    openEditEngine,
  };
};
